/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { useFlags } from '@atlaskit/flag';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionContext,
	ActionRendererFor,
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../../common/utils/buildToolFromAction';
import { useEditorPreset } from '../../jira-issue-create-v2/issue-create-suggestion-preview/issue-description-editor';
import { createFieldInfoFromResponse } from '../../jsm-create-request/utils';

import messages from './messages';

const styles = cssMap({
	negativeMarginTop: {
		marginTop: token('space.negative.150'),
	},
	fieldsWrapper: {
		width: '100%',
	},
	fieldLabel: {
		flexShrink: 0,
	},
});

export type JsmLivechatFormPayload = {
	summary?: string;
	description?: string;
};

// Fields that should be hidden from the UI but kept in the payload
const HIDDEN_FIELDS = ['serviceDeskId', 'ServiceDeskId', 'requestTypeId', 'RequestTypeId'];

const filterHiddenFields = (requestFieldValues: Record<string, unknown>) =>
	Object.fromEntries(
		Object.entries(requestFieldValues).filter(([key]) => !HIDDEN_FIELDS.includes(key)),
	);

type JsmLivechatActionRendererProps = ActionRendererFor<typeof ActionKeys.JsmLivechat>;

export const JsmLivechatActionRenderer = ({
	onCustomDataActioned: _onCustomDataActioned,
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	ContentRenderer,
	productKey,
	getCancelButton,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: JsmLivechatActionRendererProps): JSX.Element => {
	di(useFlags);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { showFlag } = useFlags();

	const action = actions[0];
	const status = getStatus(action);
	const readonly = getReadonly(action);
	const isActionDisabled = status !== 'idle' || readonly;

	const actionData = action.data;

	// Extract summary and description from either nested requestFieldValues or flat structure
	const getSummary = () => {
		return actionData.requestFieldValues?.summary || actionData.summary;
	};

	const getDescription = () => {
		return actionData.requestFieldValues?.description || actionData.description;
	};

	// Extract field information, filtering out serviceDeskId and requestTypeId from display
	const fieldInfo = actionData.requestFieldValues
		? createFieldInfoFromResponse(
				filterHiddenFields(actionData.requestFieldValues),
				(action.metadata?.requestFieldNames as Record<string, string> | undefined) ||
					actionData.requestFieldNames,
			)
		: [];

	const [inlineFormData, setInlineFormData] = useState<JsmLivechatFormPayload>({
		...(getSummary() && { summary: getSummary() }),
		...(getDescription() && { description: getDescription() }),
	});

	const handleStartChat = useCallback(async () => {
		if (isActionDisabled) {
			return;
		}

		// TODO: Remove this try/catch once the tools resumption migration is cleaned up
		// (kill switch and experiment removed, along with the legacy path).
		try {
			// Keep serviceDeskId and requestTypeId in the payload
			// but they're hidden from the UI
			const updatedData = {
				...actionData,
				...(actionData.requestFieldValues
					? {
							requestFieldValues: {
								...actionData.requestFieldValues,
								...(inlineFormData.summary && { summary: inlineFormData.summary }),
								...(inlineFormData.description && { description: inlineFormData.description }),
							},
						}
					: {
							...(inlineFormData.summary && { summary: inlineFormData.summary }),
							...(inlineFormData.description && { description: inlineFormData.description }),
						}),
			};

			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'jsmLivechatActionRenderer',
					},
					confirmed_tools: {
						message_id: messageId ?? '',
						tools: plannedActions.map((queuedAction) =>
							queuedAction.invocationId === action.invocationId
								? buildToolFromAction(queuedAction, 'confirmed', updatedData)
								: buildToolFromAction(queuedAction, 'cancelled'),
						),
					},
				});
				return;
			}

			await onCustomDataConfirm(action, updatedData);
		} catch {
			showFlag({
				id: 'jsm-livechat-error',
				title: formatMessage(messages.jsmLivechatErrorTitle),
				description: formatMessage(messages.jsmLivechatErrorDescription),
				appearance: 'error',
				isAutoDismiss: true,
			});
		}
	}, [
		isActionDisabled,
		action,
		actions,
		actionData,
		inlineFormData,
		onCustomDataConfirm,
		showFlag,
		formatMessage,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		getStatus,
	]);

	return (
		<Stack space="space.200" xcss={styles.negativeMarginTop}>
			<Text>
				{formatMessage(messages.jsmLivechatInstruction, {
					startChatButtonCopy: <Text as="strong">{formatMessage(messages.startChatButton)}</Text>,
				})}
			</Text>

			<ActionCardWrapper title={formatMessage(messages.jsmLivechatTitle)} product="jsm">
				{/* Livechat Preview with Inline Editing */}
				<JsmLivechatSuggestionPreview
					formData={inlineFormData}
					setFormData={setInlineFormData}
					isDisabled={isActionDisabled}
					action={action}
					productKey={productKey}
					cloudId={cloudId}
					ContentRenderer={ContentRenderer}
					messageId="test-message-id"
				/>

				{fieldInfo.length > 0 && (
					<>
						<Stack space="space.100">
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.additionalFieldsLabel)}
							</Text>
							{fieldInfo.map((field) => (
								<Flex key={field.id} direction="column" gap="space.050">
									<Box xcss={styles.fieldLabel}>
										<Text weight="semibold" size="small">
											{field.label}:
										</Text>
									</Box>
									<Box>
										{field.type === 'doc' ? (
											<ContentRenderer
												// eslint-disable-next-line @typescript-eslint/no-explicit-any
												content={field.value as any}
												productKey={productKey}
												siteId={cloudId}
											/>
										) : (
											<Text size="small">{field.displayValue || 'Not set'}</Text>
										)}
									</Box>
								</Flex>
							))}
						</Stack>
					</>
				)}

				{/* Action Buttons */}
				<ActionCardFooter>
					<Inline space="space.100">
						<Button
							type="submit"
							isDisabled={isActionDisabled}
							isLoading={status === 'started'}
							onClick={(_, e) => {
								handleStartChat();
								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'rovoJsmLivechatSubmit',
									}),
								);
							}}
							interactionName="ai-mate.jsm-livechat"
						>
							{formatMessage(messages.startChatButton)}
						</Button>
						{getCancelButton(action, isActionDisabled)}
					</Inline>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};

type JsmLivechatSuggestionPreviewProps = {
	action: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JsmLivechat]>;
	cloudId: string;
	ContentRenderer: JsmLivechatActionRendererProps['ContentRenderer'];
	formData: JsmLivechatFormPayload;
	setFormData: (formData: JsmLivechatFormPayload) => void;
	isDisabled: boolean;
};

const JsmLivechatSuggestionPreview = ({
	action: _action,
	ContentRenderer,
	formData,
	setFormData,
	isDisabled,
	productKey,
	cloudId,
}: JsmLivechatSuggestionPreviewProps & ActionContext) => {
	const { formatMessage } = useIntl();

	const handleOnChange = (fieldName: keyof JsmLivechatFormPayload, fieldValue: string | null) => {
		setFormData({
			...formData,
			[fieldName]: fieldValue,
		});
	};

	const { editorApi, preset } = useEditorPreset({
		productKey,
		siteId: cloudId,
	});

	return (
		<Inline space="space.150">
			<Flex gap="space.150" direction="column" xcss={styles.fieldsWrapper}>
				{formData.summary && (
					<Flex direction="column">
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.summaryLabel)}
						</Text>
						<Text>
							{isDisabled ? (
								<span>{formData.summary}</span>
							) : (
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								<InlineEdit<any>
									testId="jsm-livechat-summary-field"
									defaultValue={formData.summary}
									editView={({ ...fieldProps }) => <Textfield {...fieldProps} isCompact />}
									readView={() => <Text>{formData.summary}</Text>}
									onConfirm={(value) => handleOnChange('summary', value)}
									readViewFitContainerWidth
								/>
							)}
						</Text>
					</Flex>
				)}

				{formData.description && (
					<Flex direction="column">
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.descriptionLabel)}
						</Text>
						<Text>
							{isDisabled ? (
								<ContentRenderer
									content={formData.description}
									productKey={productKey}
									siteId={cloudId}
								/>
							) : (
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								<InlineEdit<any>
									testId="jsm-livechat-description-field"
									readViewFitContainerWidth
									defaultValue={formData.description}
									keepEditViewOpenOnBlur={false}
									editView={(fieldProps, ref) => (
										<Box ref={ref}>
											<ComposableEditor
												minHeight={50}
												shouldFocus={true}
												preset={preset}
												maxHeight={160}
												appearance="chromeless"
												onChange={(_ev) => {
													editorApi?.core.actions.requestDocument((doc) => {
														const updatedDoc = doc?.content.length ? doc : null;
														fieldProps.onChange(updatedDoc);
													});
												}}
												onEditorReady={(actions) => {
													if (formData.description) {
														actions.replaceDocument(encodeAdf(formData.description));
													}
												}}
											/>
										</Box>
									)}
									readView={() => (
										<ContentRenderer
											content={formData.description!}
											productKey={productKey}
											siteId={cloudId}
										/>
									)}
									onConfirm={(value) => {
										handleOnChange('description', value);
									}}
								/>
							)}
						</Text>
					</Flex>
				)}
			</Flex>
		</Inline>
	);
};
